<template>
  <div>
    <b-breadcrumb :items="breadcrumbItems"></b-breadcrumb>

    <div class="row mb-3">
      <div class="col-sm-4">
        <h4 qid="organizations-page-title" class="mb-2">
          {{ $t('maintenance.organizations.title') }}
        </h4>
        <div>
          <span v-if="getGuide('organizations_list')">
            <GuideButton :guide="getGuide('organizations_list')"></GuideButton>
          </span>
        </div>
      </div>
      <div class="col-sm-8 text-right">
        <router-link
            to="/maintenance/organizations/create"
            class="btn btn-success float-right"
            qid="create-org-button"
            v-if="isGranted(permissions.ORGANIZATION_CREATE)"
        >
          <font-awesome-icon icon="plus-circle"/> {{ $t('maintenance.organizations.create_button') }}
        </router-link>
        <b-form-input
            type="text"
            v-model="filters.keywordValue"
            class="float-right mw-300 mr-3 search-bar"
            v-on:input="debounceInput"
            qid="search-org-input"
            :placeholder="$t('maintenance.organizations.search_placeholder')"
        ></b-form-input>
      </div>
    </div>

    <div class="row">
      <div class="col-sm-12 table-container">
        <div>
          <OrganizationTreeListView></OrganizationTreeListView>
        </div>
      </div>
    </div>


    <!-- Delete third party confirm modal template -->
    <b-modal ref="delete-organization" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-org-title">
          {{ $t('maintenance.organizations.delete.title') }}
        </h4>
      </div>
      <h5 class="text-center">
        {{ $t('maintenance.organizations.delete.description') }} {{ itemToDelete.name }}
      </h5>
      <h5 class="text-center">
        {{ $t('maintenance.organizations.delete.are_you_sure') }}
      </h5>
      <div class="text-center">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="hideConfirmDeleteModal"
                  qid="delete-org-cancel-button"
        >
          {{ $t('maintenance.organizations.delete.cancel') }}
        </b-button>
        <b-button @click="confirmDeleteTP(itemToDelete)"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-org-submit-button"
                  v-if="isGranted([permissions.ORGANIZATION_DELETE, permissions.ORGANIZATION_DELETE_OWN], itemToDelete.created_by) && (!getClient.parent || (getClient.parent && itemToDelete.partition))"
        >
          {{ $t('maintenance.organizations.delete.submit') }}
        </b-button>
      </div>
    </b-modal>


    <!-- Edit organization modal template -->
    <b-modal ref="edit-organization" hide-footer>
      <div class="d-block text-center">
        <h4 qid="update-org-title">
          {{ $t('maintenance.organizations.update.title') }}
        </h4>
      </div>
      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="create-org-error"
      >
        {{ error }}
      </b-alert>
      <b-form @submit="editTPFormSubmit" qid="edit-org-form">
        <div class="form-container">
          <b-form-group>
            <label qid="edit-org-name-label">
              {{ $t('maintenance.organizations.create.name_label') }}
            </label>
            <b-form-input
              qid="edit-org-form-name"
              type="text"
              v-model="tpForm.name"
              :maxLength="255"
              required
              :placeholder="$t('maintenance.organizations.create.name_placeholder')"
            ></b-form-input>
          </b-form-group>
          <b-form-group>
            <label qid="edit-org-description-label">
              {{ $t('maintenance.organizations.create.description_label') }}
            </label>
            <b-form-textarea
              qid="edit-org-form-description"
              v-model="tpForm.description"
              rows="6"
              max-rows="6"
              :placeholder="$t('maintenance.organizations.create.description_placeholder')"
            ></b-form-textarea>
          </b-form-group>
          <b-form-group>
            <label qid="edit-org-organization-level-label">
              {{ $t('maintenance.organizations.create.organization_level_label') }}
            </label>
            <multiselect
                v-model="tpForm.organization_level"
                :options="levelItems"
                :placeholder="$t('maintenance.organizations.create.organization_level_placeholder')"
                label="name"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                qid="edit-org-form-organization-level"
                @select="selectLevel"
                @remove="removeLevel"
            ></multiselect>
          </b-form-group>
          <b-form-group ref="select-link-organization" :class="{'d-none':!showLinkOrganizations}">
            <label qid="edit-org-link-organization-label">
              {{ $t('maintenance.organizations.create.link_organization_label') }}
            </label>
            <multiselect
                v-model="tpForm.link_organization"
                :options="organizations"
                track-by="id"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                @search-change="debounceOrganizations"
                :placeholder="$t('maintenance.organizations.create.link_organization_placeholder')"
                label="name"
                qid="edit-org-link-organization-select"
            ></multiselect>
          </b-form-group>

          <b-form-group>
            <label qid="create-p-users-label">
              {{ $t('maintenance.organizations.create.members_label') }}
            </label>
            <multiselect
                v-model="selectedUsers"
                :options="users"
                :multiple="true"
                :taggable="false"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                @search-change="loadUsers"
                track-by="slug"
                :placeholder="$t('maintenance.organizations.create.members_placeholder')"
                label="name"
                qid="create-p-users-select"
            >
              <template slot="tag" slot-scope="props">
                <span class="mr-3 user-tag">
                  <Avatar :object="props.option" size="xs"></Avatar>
                  <span class="ml-2">{{ props.option.name }}</span>
                </span>
              </template>
              <template slot="option" slot-scope="props">
                <Avatar :object="props.option" size="xs"></Avatar>
                <span class="ml-2">
                  {{ props.option.name }}
                </span>
              </template>
            </multiselect>
          </b-form-group>

        </div>
        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideEditTPModal"
                    qid="edit-org-cancel-button"
          >
            {{ $t('maintenance.organizations.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    class="mt-3 ml-3"
                    :disabled="buttonDisabled"
                    variant="success"
                    qid="edit-org-submit-button"
          >
            <span v-if="buttonDisabled" class="mr-1">
              <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
            </span>
            {{ $t('maintenance.organizations.create.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <ShowMoreModal
            :items="selectedItem.users"
            :show="showUsersModal"
            @closeViewModal="closeUsersModal"
            type="users"
    ></ShowMoreModal>

  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import Avatar from '@/components/Avatar.vue';
  import ShowMoreModal from '@/components/ShowMoreModal.vue';
  import OrganizationTreeListView from '@/components/organizations/OrganizationTreeListView.vue';
  import GuideButton from '@/components/guide/GuideButton.vue';


  export default {
    name: 'Organizations',
    data: function() {
      return {
        breadcrumbItems: [
          {
            text: this.$t('dashboard.title'),
            to: '/'
          },
          {
            text: this.$t('maintenance.title'),
            to: '/maintenance'
          },
          {
            text: this.$t('maintenance.organizations.title'),
            active: true
          }
        ],
        tableItems: [],

        // Pagination
        perPageOptions: [
          { name: '10', value: '10' },
          { name: '20', value: '20' },
          { name: '50', value: '50' },
          { name: '100', value: '100' }
        ],
        perPage: '',
        selectedPerPage: "10",
        currentPage: 1,
        totalRows: 0,

        itemToDelete: {},
        tpForm: {
          name: '',
          description: '',
          organization_level: '',
          link_organization: '',
          client:'',
          users: []
        },
        error: '',
        selectedSort: '',
        selectedDirection: '',
        levelItems: [
          { name: this.$t('system.level') + ' 1', value: '1' },
          { name: this.$t('system.level') + ' 2', value: '2' },
          { name: this.$t('system.level') + ' 3', value: '3' },
          { name: this.$t('system.level') + ' 4', value: '4' },
          { name: this.$t('system.level') + ' 5', value: '5' }
        ],
        organizations: [],
        selectedOrganizations: [],
        filterOpened: false,
        keywordValue: '',
        filters: {
          organization_level: '',
          keywordValue: ''
        },
        showLinkOrganizations: false,
        selected_organization: '',
        loading: true,
        buttonDisabled: false,
        selectedUsers: [],
        loadingItems: false,
        users: [],
        selectedItem: {},
        showUsersModal: false,
        myGuides: [],
      }
    },
    components: {
      Avatar,
      ShowMoreModal,
      OrganizationTreeListView,
      GuideButton
    },
    computed: {
      rows() {
        // Use computed value for rows because of dynamic update
        return this.totalRows
      },
      getClient() {
        return this.$store.state.client
      },
      getFilters() {
        return this.$store.state.activeFilters
      },
      getStorePerPage() {
        return this.$store.state.perPage
      },
      getPerPage() {
        let page = this.currentPage;
        let selectedFrom = (page-1) * Number(this.selectedPerPage) + 1;
        let selectedTo = (page-1) * Number(this.selectedPerPage) + Number(this.selectedPerPage);
        let selectedOf = this.totalRows;

        if(selectedTo > selectedOf){
          selectedTo = selectedOf
        }

        return selectedFrom + " - " + selectedTo + " " + this.$t('system.of') + " " + selectedOf
      }
    },
    watch: {
      // Watch when client changes in store and trigger api reload
      getClient: function () {
        this.loadContent();
      }
    },
    methods: {
      debounceInput: _.debounce(function() {
        this.loadContent()
      }, 400),
      debounceOrganizations: _.debounce(function(query) {
        this.asyncLoadLinkOrganizations(query)
      }, 400),
      getGuide(code) {
        let existingGuide = _.find(this.myGuides, function(object) {
          return object.scope == code && object.type == 'Maintenance' && object.enabled
        });
        return existingGuide
      },
      loadGuides(code) {
        let self = this;
        let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

        piincHttp.get('guides', { params:
          {
            per_page: 1,
            language: 'en',
            type: 'Maintenance',
            scope: code,
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.myGuides.push(response.data.items[0])
          }
        }, function() {});
      },
      resetFilters() {
        this.filters.type = '',
        this.filters.organization_level = ''
        this.debounceInput()
      },
      openUsersModal(object) {
        this.selectedItem = object
        this.showUsersModal = true
      },
      closeUsersModal() {
        this.selectedItem = {}
        this.showUsersModal = false
      },
      selectLevel(selectedOption) {
        this.tpForm.organization_level = selectedOption
        if (selectedOption && selectedOption.value != 1) {
          this.showLinkOrganizations = true;
        } else {
          this.showLinkOrganizations = false;
        }
        this.asyncLoadLinkOrganizations()
      },
      removeLevel() {
        this.tpForm.link_organization = ''
        this.showLinkOrganizations = false
        this.tpForm.organization_level = ''
      },
      asyncLoadLinkOrganizations(query) {

        let self = this;
        piincHttp.get('organizations', { params:
          {
            smaller_then_organization_level: this.tpForm.organization_level.value,
            keyword: query ? query : undefined,
            exclude_organization: this.selected_organization.id,
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.organizations = response.data.items;
          }
        }, function() {});
      },
      selectSort(sort) {
        // If old and new sort are same, switch just direction
        if (sort === this.selectedSort) {
          this.selectedDirection = this.selectedDirection === 'desc' ? 'asc' : 'desc';
        } else {
          // If different sort, set sort and set direction to default
          this.selectedDirection = 'desc'
          this.selectedSort = sort;
        }
        this.loadContent();
      },
      loadContent(page, perPage) {

        if (this.getStorePerPage) {
          this.selectedPerPage = this.getStorePerPage;
        }

        // If page is not defined, set page to default 1
        let pageNumber = page ? page : 1;
        let perPageNumber = perPage ? perPage : this.selectedPerPage;
        let self = this;

        if (
          this.filters.keywordValue !== '' ||
          (this.filters.organization_level && this.filters.organization_level.value)
        ) {
          this.filtersOpened = true
        }

        piincHttp.get('organizations', { params:
          {
            tree: 1,
            per_page: 100,
            sort: this.selectedSort !== '' ? this.selectedSort : undefined,
            order: this.selectedDirection !== '' ? this.selectedDirection : undefined,
            keyword: this.filters.keywordValue !== '' ? this.filters.keywordValue : undefined,
            organization_level: this.filters.organization_level ? this.filters.organization_level.value : undefined,
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.loading = false;
          self.tableItems = response.data.items;
          // Set number of total items
          self.totalRows = response.data.total_count;
        }, function() {});
      },
      pageChanged(page) {
        this.loadContent(page)
      },
      perPageChanged(perPage) {
        let self = this;
        // Load content when page changed listener is triggered
        this.loadContent(1, perPage.value)
        self.selectedPerPage = perPage.value
      },
      perPageRemoved() {
        let self = this;
        // Load content when page changed listener is triggered
        self.selectedPerPage = self.perPageOptions[0].value
        this.loadContent(1, self.selectedPerPage)
      },
      showConfirmDeleteModal(object) {
        this.itemToDelete = object
        this.$refs['delete-organization'].show()
      },
      hideConfirmDeleteModal() {
        this.itemToDelete = {}
        this.$refs['delete-organization'].hide()
      },
      showEditTPModal(object) {

        let self = this;
        this.error = ''
        this.selected_organization = object;

        this.loadUsers();

        this.selectedUsers = []
        if (object.users && object.users.length) {
          _.forEach(object.users, function (user) {
            self.selectedUsers.push(user)
          })
        }

        this.tpForm.link_organization = []
        this.tpForm.organization_level = []
        if (object.organization_level) {
          this.tpForm.organization_level = _.find(this.levelItems, function(item) {
            return item.value == object.organization_level;
          });
          this.showLinkOrganizations = object.organization_level == 1 ? false : true;
          this.asyncLoadLinkOrganizations();
          this.tpForm.link_organization = object.linkedOrganization;
        }

        this.tpForm.slug = object.slug;
        this.tpForm.name = object.name;
        this.tpForm.description = object.description;

        this.$refs['edit-organization'].show()
      },
      hideEditTPModal() {
        this.$refs['edit-organization'].hide()
      },
      editTPFormSubmit(evt) {
        this.buttonDisabled = true;
        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();
        this.tpForm.client = this.getClient.slug;
        this.tpForm.organization_level = this.tpForm.organization_level ? this.tpForm.organization_level.value : undefined;
        this.tpForm.link_organization = this.tpForm.link_organization ? this.tpForm.link_organization.id : '';

        this.tpForm.users = [];
        if (this.selectedUsers && this.selectedUsers.length) {
          for (var i = 0; i < this.selectedUsers.length; i++) {
            this.tpForm.users.push(this.selectedUsers[i].id);
          }
        }

        piincHttp.put('organizations/' + this.tpForm.slug, this.tpForm).then(function(response) {
          self.hideEditTPModal();
          self.loadContent(self.currentPage);
          self.buttonDisabled = false;
          self.$toastr('success', self.$t('maintenance.organizations.update.success'))
        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            self.error = error.data.message;
            if (error.data.errors.organization_level) {
              self.error += ' ' + error.data.errors.organization_level[0]
            }
          }
        })
      },
      getReducedUsers(users) {
        let reducedUsers = []
        if (users.length > 3) {
          reducedUsers = _.cloneDeep(users);
          reducedUsers = reducedUsers.slice(0, 3);
        } else {
          reducedUsers = users;
        }
        return reducedUsers;
      },
      confirmDeleteTP(object) {
        let self = this;
        piincHttp.delete('organizations/' + object.slug).then(function(response) {
          self.hideConfirmDeleteModal();
          self.loadContent(self.currentPage);
          // Trigger toastr message after modal close
          self.$toastr('success', self.$t('maintenance.organizations.delete.success'))
        }, function() {});
      },
      loadUsers(query) {
        let self = this;
        piincHttp.get('users', { params:
          {
            client: this.getClient.id,
            keyword: query ? query : undefined,
            per_page: 50
          }
        }).then(function(response) {
          self.users = response.data.items
          if (self.users && !self.users.length && !query) {
            self.users.push({name: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      }
    },
    beforeRouteLeave(to, from, next) {
      let object = {
        type: 'organizations',
        filters: this.filters
      }
      this.$store.commit('storeActiveFilters', object);
      this.$store.commit('storePerPage', this.selectedPerPage);
      next()
    },
    created () {
      if (this.getClient && this.getClient.slug) {
        if (this.getFilters.organizations) {
          this.filters = this.getFilters.organizations
        }
        this.loadContent();
      }
      this.loadGuides('organizations_list');
    }
  }
</script>
